import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { Mail, Building2, Phone, Calendar, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/contact/submissions`);
      setSubmissions(response.data.submissions);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError('Failed to load submissions');
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) {
      return;
    }
    setDeleting(id);
    try {
      await axios.delete(`${API}/contact/submissions/${id}`);
      setSubmissions(submissions.filter(s => s.id !== id));
    } catch (err) {
      console.error('Error deleting submission:', err);
      alert('Failed to delete submission');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-BE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceLabel = (value) => {
    const labels = {
      'business-development': 'Business Development',
      'hr-integration': 'HR Integration',
      'both': 'Both Services',
      'other': 'Other'
    };
    return labels[value] || value;
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <h1 className="admin-title">Contact Form Submissions</h1>
          <p className="admin-loading">Loading submissions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <h1 className="admin-title">Contact Form Submissions</h1>
          <p className="admin-error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <h1 className="admin-title">Contact Form Submissions</h1>
          <Badge className="admin-badge">{submissions.length} Total</Badge>
        </div>

        {submissions.length === 0 ? (
          <Card className="empty-state">
            <p>No submissions yet.</p>
          </Card>
        ) : (
          <div className="submissions-grid">
            {submissions.map((submission) => (
              <Card key={submission.id} className="submission-card">
                <div className="submission-header">
                  <h3 className="submission-name">{submission.name}</h3>
                  <div className="submission-actions">
                    <Badge className={`status-badge status-${submission.status}`}>
                      {submission.status}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(submission.id)}
                      disabled={deleting === submission.id}
                      className="delete-btn"
                      data-testid={`delete-btn-${submission.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>

                <div className="submission-details">
                  <div className="detail-row">
                    <Mail size={16} />
                    <a href={`mailto:${submission.email}`} className="detail-link">
                      {submission.email}
                    </a>
                  </div>

                  <div className="detail-row">
                    <Building2 size={16} />
                    <span>{submission.company}</span>
                  </div>

                  {submission.phone && (
                    <div className="detail-row">
                      <Phone size={16} />
                      <a href={`tel:${submission.phone}`} className="detail-link">
                        {submission.phone}
                      </a>
                    </div>
                  )}

                  <div className="detail-row">
                    <Calendar size={16} />
                    <span>{formatDate(submission.submittedAt)}</span>
                  </div>
                </div>

                <div className="submission-service">
                  <strong>Service Interest:</strong> {getServiceLabel(submission.serviceInterest)}
                </div>

                <div className="submission-message">
                  <strong>Message:</strong>
                  <p>{submission.message}</p>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubmissions;
